/**
 * Premium Value Indicator
 * Subtle indicator in navigation that shows when premium features would be valuable
 * Uses scarcity and FOMO without being aggressive
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { Sparkles, ChevronDown, CheckCircle2 } from 'lucide-react';
import { MotionDiv } from '../lib/motion-utils';

export function PremiumValueIndicator() {
  const navigate = useNavigate();
  const location = useLocation();
  const [shouldShow, setShouldShow] = useState(false);
  const [message, setMessage] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [quickWinIdx, setQuickWinIdx] = useState(0);

  useEffect(() => {
    // Check if user is already premium
    const subscription = localStorage.getItem('tension_subscription');
    if (subscription && subscription !== 'free') {
      setShouldShow(false);
      return;
    }

    // Determine if we should show the indicator based on context
    const checkins = JSON.parse(localStorage.getItem('tension_checkins') || '[]');
    const lastCheckin = checkins[checkins.length - 1];
    const lastRTI = lastCheckin?.rtiScore || 0;

    // High tension - forecasting would be valuable
    if (lastRTI > 70 && checkins.length >= 3) {
      setMessage('Forecast available');
      setShouldShow(true);
      return;
    }

    // 7+ day streak - pattern detection available
    if (checkins.length >= 7) {
      setMessage('Patterns detected');
      setShouldShow(true);
      return;
    }

    // User has shown interest before
    const hasInterest = localStorage.getItem('tension_upgrade_interest') === 'true';
    if (hasInterest && checkins.length >= 3) {
      setMessage('Unlock insights');
      setShouldShow(true);
      return;
    }

    setShouldShow(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!shouldShow || !expanded) return;
    const tips = ['Forecast next 72 hours', 'Unlock pattern detection', 'Get action cards'];
    const timer = setInterval(() => {
      setQuickWinIdx((idx) => (idx + 1) % tips.length);
    }, 2500);
    return () => clearInterval(timer);
  }, [shouldShow, expanded]);

  if (!shouldShow) return null;

  return (
    <MotionDiv
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="space-y-2"
    >
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-violet-500/10 to-blue-500/10 hover:from-violet-500/20 hover:to-blue-500/20 border border-violet-300 dark:border-violet-700 transition-all group"
      >
        <Sparkles className="w-3.5 h-3.5 text-violet-600 dark:text-violet-400 group-hover:animate-pulse" />
        <span className="text-xs font-medium text-violet-700 dark:text-violet-300">
          {message}
        </span>
        <div className="w-1.5 h-1.5 rounded-full bg-violet-600 dark:bg-violet-400 animate-pulse" />
        <ChevronDown className={`w-3.5 h-3.5 text-violet-600 dark:text-violet-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded ? (
        <div className="rounded-xl border border-violet-300/60 dark:border-violet-700/60 bg-violet-50/70 dark:bg-violet-950/20 p-3 space-y-2">
          <div className="flex items-center gap-2 text-xs text-violet-800 dark:text-violet-200">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>{['Forecast next 72 hours', 'Unlock pattern detection', 'Get action cards'][quickWinIdx]}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-md bg-violet-600 text-white text-xs px-2.5 py-1.5 hover:bg-violet-700"
              onClick={() => navigate('/pricing')}
            >
              See plans
            </button>
            <button
              type="button"
              className="rounded-md border border-violet-300 dark:border-violet-700 text-xs px-2.5 py-1.5 text-violet-700 dark:text-violet-300"
              onClick={() => setExpanded(false)}
            >
              Later
            </button>
          </div>
        </div>
      ) : null}
    </MotionDiv>
  );
}
